<template>
  <v-card>
    <v-card-title>
      <v-toolbar v-if="$vuetify.breakpoint.mobile" flat class="pb-0 modal-action-toolbar">
        <v-toolbar-title>
          {{ $t('Edit profile') }}
        </v-toolbar-title>

        <v-btn icon @click="$emit('close')">
          <v-icon class="heading grey--text text--darken-4">
            mdi-arrow-left
          </v-icon>
        </v-btn>
      </v-toolbar>

      <span v-else class="text-h5">
        {{ $t('edit user') }}
      </span>
    </v-card-title>

    <v-card-text class="pb-0 px-3">
      <v-container class="half-height pb-0">
        <p v-if="$vuetify.breakpoint.mobile" class="profile-title mb-4">
          {{ $t('My Profile') }}
        </p>

        <form-wrapper :validator="$v.editedItem" :messages="localMessages" :attributes="attributes">
          <form-group name="id">
            <template slot-scope="{attrs}">
              <v-text-field
                v-model="editedItem.id"
                v-bind="attrs"
                :label="$t('username')"
                :dense="!$vuetify.breakpoint.mobile"
                :disabled="!isNew"
                class="settings-field"
                outlined
                required @input="$v.editedItem.id.$touch"
              />
            </template>
          </form-group>

          <form-group name="name">
            <template slot-scope="{attrs}">
              <v-text-field
                v-model="editedItem.name"
                v-bind="attrs"
                :label="$t('name')"
                :dense="!$vuetify.breakpoint.mobile"
                required outlined
                class="settings-field"
              />
            </template>
          </form-group>

          <form-group name="email">
            <template slot-scope="{attrs}">
              <v-text-field
                v-model="editedItem.email"
                v-bind="attrs"
                :dense="!$vuetify.breakpoint.mobile"
                :label="$t('email')"
                required outlined
                class="settings-field"
              />
            </template>
          </form-group>

          <form-group name="phoneNumber">
            <template slot-scope="{attrs}">
              <v-text-field
                id="phoneNumber"
                v-model="editedItem.phoneNumber"
                :label="$t('phoneNumber')"
                :dense="!$vuetify.breakpoint.mobile"
                v-bind="attrs"
                outlined
                class="settings-field"
              />
            </template>
          </form-group>

          <form-group name="languageId">
            <template slot-scope="{attrs}">
              <language-select
                v-model="editedItem.languageId"
                :label="$t('language')"
                :dense="!$vuetify.breakpoint.mobile"
                v-bind="attrs"
                outlined required
                class="settings-field"
              />
            </template>
          </form-group>

          <inline-editable-delegate-table
            :user-id="userId"
            class="mb-10 mt-4"
            @delegation-validation-error="updateDelegateValidationError"
            @delegation-validation-error-cleared="clearDelegateValidationError"
            @delegatesUpdated="delegatesUpdated"
          />
        </form-wrapper>
      </v-container>

      <v-alert
        v-if="error"
        border="top"
        color="red lighten-2"
        dark
      >
        {{ $t(error) }}
      </v-alert>
    </v-card-text>

    <div v-if="delegateValidationError" class="error-wrapper px-5 py-1">
      <p class="red--text mt-4 row-without-bottom-line">
        {{ $t(delegateValidationError) }}
      </p>
    </div>

    <v-card-actions class="actions pb-4 mr-2">
      <v-spacer />
      <v-btn
        v-if="!$vuetify.breakpoint.mobile"
        color="error"
        text
        @click="$emit('close')"
      >
        {{ $t('ui.cancel') }}
      </v-btn>

      <v-btn
        color="primary"
        dark
        @click="save(editedItem)"
      >
        {{ $t('save') }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>

import { required, minLength, email } from 'vuelidate/lib/validators';
import { templates } from 'vuelidate-error-extractor';
import LanguageSelect from '@/atoms/LanguageSelect.vue';
import InlineEditableDelegateTable from '@/atoms/InlineEditableDelegateTable.vue';

import FormGroup from '@/atoms/FormGroup.vue';
import DelegateApi from '@/api/DelegateApi';

const delegateApi = new DelegateApi();

const isPhoneNumber = (v) => !v || /^$|^\+\d{5,15}$/.test(v);

export default {
  name: 'UserForm',
  components: {
    FormGroup, LanguageSelect, FormWrapper: templates.FormWrapper, InlineEditableDelegateTable,
  },
  validations: {
    editedItem: {
      id: { required, minLength: minLength(5) },
      name: { required },
      languageId: { required },
      email: { required, email },
      phoneNumber: { isPhoneNumber },
    },
  },
  props: {
    isNew: {
      type: Boolean,
    },
    dialogOpened: {
      type: Boolean,
    },
    error: { type: String, default: '' },
  },
  data() {
    return {
      localMessages: {
        email: this.$t('Invalid email format'),
        required: this.$t('Field {attribute} should be filled out.'),
        minLength: this.$t('Needs a longer '),
        isPhoneNumber: this.$t('Not a valid number'),
      },
      attributes: {
        id: this.$t('username'),
        name: this.$t('full name'),
        email: this.$t('user Email'),
        phoneNumber: this.$t('phonenumber'),
      },
      delegateValidationError: null,
      addedUpdatedDelegates: [],
    };
  },
  computed: {
    editedItem() {
      return this.$store.getters.user;
    },
    userId() {
      return this.editedItem.id;
    },

  },
  watch: {
    dialogOpened(val) {
      if (val) {
        this.$v.$reset();
      }
    },
  },
  methods: {
    save() {
      this.$v.$touch();
      this.addUserDelegates().then(() => {
        if (this.$v.$invalid) {
          this.submitStatus = 'ERROR';
        } else {
          const currentLang = this.$store.getters.locale;
          this.$store.dispatch('SaveUser', this.editedItem).then(() => {
            this.$emit('close');
            if (currentLang !== this.editedItem.languageId) {
              window.location.reload();
            }
          });
        }
      })
        .catch(() => {
          this.submitStatus = 'ERROR';
        });
    },

    addUserDelegates() {
      if (this.addedUpdatedDelegates && this.addedUpdatedDelegates.length > 0) {
        return delegateApi.createDelegates(this.addedUpdatedDelegates);
      }
      return Promise.resolve();
    },

    delegatesUpdated(delegatesList) {
      this.addedUpdatedDelegates = delegatesList;
    },
    updateDelegateValidationError(validationError) {
      console.log('delegateValidationError');
      this.delegateValidationError = validationError;
    },
    clearDelegateValidationError() {
      this.delegateValidationError = null;
    },
  },
};
</script>

<style lang="scss" scoped>
.error-wrapper {
  position: relative !important;
  z-index: 40 !important;
}
.half-height {
  max-height: 90%;
  &.with-errors {
    max-height: 70%;
  }
  overflow-x: hidden;
  overflow-y: auto;
}
.v-card__text {
  min-height: 60vh;
  max-height: 75vh;
  overflow-x: hidden;
}
.row-without-bottom-line {
  border-bottom: white 0;
}

.btn-border {
  border-width: 2pt !important;
}
//RESPONSIVENESS
@media (max-width: 710px) {
  .v-card {
    display: block;
    overflow: hidden !important;
  }
  .v-card__title {
    padding: 0 8px !important;
  }
  .profile-title {
    font-weight: 500;
    font-size: 17px;
    color: $yester-dark;
  }
  .description-text {
    color: $yester-dark;
    font-weight: 400;
    font-size: 14px;
  }
  .v-card__text {
    padding-bottom: 0 !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
    overflow: auto;
    min-height: 20vh;
    max-height: 75vh;
  }
  .v-card__actions {
    margin-right: 0 !important;
    padding-top: 10px !important;
    padding-right: 8px !important;
    padding-left: 10px !important;
    display: block !important;
    height: 15%;
    button {
      width: 100% !important;
      margin-bottom: 10px;
      &.primary-btn {
        margin-left: 0 !important;
      }
      &:last-of-type {
        margin-bottom: 0;
      }
    }
  }
}
</style>
